/**
 * WebSocket Service for real-time notifications
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Heartbeat ping to keep connection alive
 * - Type-based subscription system
 * - Connection state tracking
 */

import { wsFirstMessageAuthEnabled } from '@/config/realtime';
import { claimsFromToken } from "@/services/auth/jwtExpiry";

const CONNECTION_STATES = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
};

class WebSocketService {
  constructor(options = {}) {
    this.url = options.url || import.meta.env.VITE_WS_URL;
    this.ws = null;
    this.connectionEpoch = 0;
    this.reconnectAttempts = 0;
    this.baseReconnectDelay = options.baseReconnectDelay || 1000;
    this.maxReconnectDelay = options.maxReconnectDelay || 30000;
    this.heartbeatInterval = null;
    this.heartbeatFrequency = options.heartbeatFrequency || 30000;
    this.listeners = new Map(); // type -> Set<callback>
    this.connectionStateListeners = new Set();
    this.connectionState = CONNECTION_STATES.DISCONNECTED;
    this.token = null;
    this.profileId = null;
    this.reconnectTimeoutId = null;
    this.authTimeoutId = null;
    this.messageQueue = []; // Queue messages when disconnected
    this.tokenProvider = null; // Callback to get fresh token/profileId for reconnection

    // Bind visibility change handler
    this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
    document.addEventListener("visibilitychange", this._handleVisibilityChange);
  }

  /**
   * Handle page visibility changes - reconnect when page becomes visible
   */
  async _handleVisibilityChange() {
    if (
      document.hidden ||
      this.connectionState !== CONNECTION_STATES.RECONNECTING
    ) {
      return;
    }
    console.log("[WebSocket] Page visible, attempting immediate reconnect");
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    const epoch = this.connectionEpoch;
    try {
      const { token, profileId } = (await this._getCredentials()) || {};
      // disconnect() may have run while we were awaiting credentials.
      if (epoch !== this.connectionEpoch || this.connectionState !== CONNECTION_STATES.RECONNECTING) return;
      if (token) {
        this.connect(token, profileId);
      } else {
        this._setConnectionState(CONNECTION_STATES.DISCONNECTED);
      }
    } catch (error) {
      console.error("[WebSocket] Visibility reconnect failed:", error);
    }
  }

  /**
   * Get current connection state
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Update connection state and notify listeners
   */
  _setConnectionState(state) {
    this.connectionState = state;
    this.connectionStateListeners.forEach((callback) => {
      try {
        callback(state);
      } catch (error) {
        console.error("[WebSocket] Error in connection state listener:", error);
      }
    });
  }

  /**
   * Subscribe to connection state changes
   * @param {Function} callback - Called with new state when it changes
   * @returns {Function} Unsubscribe function
   */
  onConnectionStateChange(callback) {
    this.connectionStateListeners.add(callback);
    // Immediately call with current state
    callback(this.connectionState);
    return () => {
      this.connectionStateListeners.delete(callback);
    };
  }

  /**
   * Set a callback to get fresh tokens for reconnection
   * @param {Function} callback - Returns { token, profileId } or a Promise<{ token, profileId }>
   */
  setTokenProvider(callback) {
    this.tokenProvider = callback;
  }

  /**
   * Get fresh credentials using provider or fall back to stored values.
   * Awaits the provider so it can do an async token refresh (e.g. tokenService.getValidAppToken).
   * @returns {Promise<{ token: string, profileId: string|number }>}
   */
  async _getCredentials() {
    if (this.tokenProvider) {
      try {
        const credentials = await this.tokenProvider();
        return credentials?.token ? credentials : null;
      } catch (error) {
        console.error("[WebSocket] Error calling token provider:", error);
        return null;
      }
    }
    return { token: this.token, profileId: this.profileId };
  }

  /**
   * Establish WebSocket connection
   * @param {string} token - JWT token for authentication
   * @param {string|number|null} profileId - Optional Amazon Ads profile ID
   */
  connect(token, profileId) {
    if (!this.url) {
      console.warn("[WebSocket] No WebSocket URL configured (VITE_WS_URL)");
      return;
    }

    // Clean up existing WebSocket
    if (this.ws) {
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        console.log("[WebSocket] Already connected or connecting");
        return;
      }

      // Cleanup closed/closing WebSocket to prevent ghost events
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws = null;
    }
    this._clearAuthTimeout();

    // Token claims are checked here but never logged: stage keeps console.log,
    // and the claims carry user and business profile identifiers.
    try {
      const tokenPayload = claimsFromToken(token);
      console.log("[WebSocket] Connecting:", { url: this.url, hasProfileId: Boolean(profileId) });

      if (tokenPayload) {
        if (tokenPayload.aud !== "WisePPCAPI") {
          console.warn(
            '[WebSocket] Warning: Token "aud" claim is not "WisePPCAPI". Backend might reject this.',
          );
        }
        if (!tokenPayload.businessProfileId) {
          console.error(
            '[WebSocket] Error: Token is missing "businessProfileId" claim. This is mandatory for the backend.',
          );
        }
      }
    } catch (e) {
      console.error("[WebSocket] Error decoding token for logs:", e);
    }

    this.token = token;
    this.profileId = profileId;
    this._setConnectionState(CONNECTION_STATES.CONNECTING);

    try {
      // Build WebSocket URL with query parameters
      const params = new URLSearchParams();
      if (token && !wsFirstMessageAuthEnabled) {
        params.append("token", token);
      }
      if (profileId) {
        params.append("profileId", profileId);
      }
      const queryString = params.toString();

      // Handle trailing slash normalization
      let baseUrl = this.url;
      if (baseUrl.endsWith("/")) {
        baseUrl = baseUrl.slice(0, -1);
      }

      let wsUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
      if (wsFirstMessageAuthEnabled) {
        const socketUrl = new URL(baseUrl);
        socketUrl.searchParams.delete('token');
        params.forEach((value, key) => socketUrl.searchParams.append(key, value));
        wsUrl = socketUrl.toString();
      }

      console.log(
        `[WebSocket] Establishing connection to: ${wsUrl.split("token=")[0]}token=...`,
      );
      const epoch = ++this.connectionEpoch;
      const socket = new WebSocket(wsUrl);
      this.ws = socket;
      const current = () => this.ws === socket && this.connectionEpoch === epoch;
      socket.onopen = (event) => { if (current()) this._handleOpen(event); };
      socket.onclose = (event) => { if (current()) this._handleClose(event); };
      socket.onerror = (event) => { if (current()) this._handleError(event); };
      socket.onmessage = (event) => { if (current()) this._handleMessage(event); };
    } catch (error) {
      console.error("[WebSocket] Connection error:", error);
      this._setConnectionState(CONNECTION_STATES.DISCONNECTED);
      this._attemptReconnect();
    }
  }

  /**
   * Handle WebSocket open event
   */
  _handleOpen() {
    if (wsFirstMessageAuthEnabled) {
      const socket = this.ws;
      const epoch = this.connectionEpoch;
      this.authTimeoutId = setTimeout(() => {
        if (this.ws !== socket || this.connectionEpoch !== epoch) return;
        this._clearAuthTimeout();
        // Retire this socket before closing so late auth/close callbacks cannot
        // complete authentication or schedule a second retry.
        this.connectionEpoch++;
        socket.close(4008, 'Authentication timeout');
        this._attemptReconnect();
      }, 10000);
      socket.send(JSON.stringify({ type: 'auth', token: this.token }));
      return;
    }

    this._markConnected();
  }

  _clearAuthTimeout() {
    if (this.authTimeoutId !== null) {
      clearTimeout(this.authTimeoutId);
      this.authTimeoutId = null;
    }
  }

  _markConnected() {
    console.log("[WebSocket] Connected");
    this._setConnectionState(CONNECTION_STATES.CONNECTED);
    this.reconnectAttempts = 0;
    this._startHeartbeat();
    this._flushMessageQueue();
  }

  /**
   * Handle WebSocket close event
   */
  _handleClose(event) {
    console.log("[WebSocket] Disconnected:", {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
    });
    this._stopHeartbeat();
    this._clearAuthTimeout();

    // 1000 is a clean close. 4001/4002 are auth closes (bad token / forbidden):
    // retrying with the same credentials loops. tokensUpdated and the
    // business-profile watcher reconnect once a fresh token or profile exists.
    if (
      event.code === 1000 ||
      event.code === 4001 ||
      event.code === 4002 ||
      this.connectionState === CONNECTION_STATES.DISCONNECTED
    ) {
      this._setConnectionState(CONNECTION_STATES.DISCONNECTED);
      return;
    }

    this._attemptReconnect();
  }

  /**
   * Handle WebSocket error event
   */
  _handleError(error) {
    console.error("[WebSocket] Error event:", error);
    // Error event is usually followed by close event, so we don't need to reconnect here
  }

  /**
   * Handle incoming WebSocket message
   */
  _handleMessage(event) {
    try {
      if (event.data === "pong") return;
      const data = JSON.parse(event.data);

      if (wsFirstMessageAuthEnabled) {
        if (data.type === 'auth_ok') {
          if (this.connectionState === CONNECTION_STATES.CONNECTING && this.authTimeoutId !== null) {
            this._clearAuthTimeout();
            this._markConnected();
          }
          return;
        }
        if (this.connectionState !== CONNECTION_STATES.CONNECTED) return;
      }

      // Handle pong response
      if (data.type === "pong") {
        return;
      }

      // Handle heartbeat from server
      if (data.type === "heartbeat" || data.type === "ping") {
        this._sendPong();
        return;
      }

      console.log("[WebSocket] Message received:", data);
      // Process notification
      this._notifyListeners(data);
    } catch (error) {
      console.error("[WebSocket] Error parsing message:", error, event.data);
    }
  }

  /**
   * Notify all subscribed listeners for a notification type
   */
  _notifyListeners(notification) {
    const { type } = notification;

    // Notify type-specific listeners
    if (type && this.listeners.has(type)) {
      this.listeners.get(type).forEach((callback) => {
        try {
          callback(notification);
        } catch (error) {
          console.error(
            `[WebSocket] Error in listener for type "${type}":`,
            error,
          );
        }
      });
    }

    // Notify wildcard listeners (subscribed to all types)
    if (this.listeners.has("*")) {
      this.listeners.get("*").forEach((callback) => {
        try {
          callback(notification);
        } catch (error) {
          console.error("[WebSocket] Error in wildcard listener:", error);
        }
      });
    }
  }

  /**
   * Subscribe to notifications of a specific type
   * @param {string} type - Notification type to subscribe to, or '*' for all
   * @param {Function} callback - Called when notification of this type arrives
   * @returns {Function} Unsubscribe function
   */
  subscribe(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(callback);

    // Return unsubscribe function
    return () => {
      this.unsubscribe(type, callback);
    };
  }

  /**
   * Unsubscribe from notifications
   */
  unsubscribe(type, callback) {
    if (this.listeners.has(type)) {
      this.listeners.get(type).delete(callback);
      if (this.listeners.get(type).size === 0) {
        this.listeners.delete(type);
      }
    }
  }

  /**
   * Send a message through the WebSocket
   * @param {Object} message - Message to send
   */
  send(message) {
    if (
      this.ws && this.ws.readyState === WebSocket.OPEN &&
      (!wsFirstMessageAuthEnabled || this.connectionState === CONNECTION_STATES.CONNECTED)
    ) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Queue message if not connected
      this.messageQueue.push(message);
    }
  }

  /**
   * Flush queued messages after reconnection
   */
  _flushMessageQueue() {
    while (
      this.messageQueue.length > 0 &&
      this.ws?.readyState === WebSocket.OPEN &&
      (!wsFirstMessageAuthEnabled || this.connectionState === CONNECTION_STATES.CONNECTED)
    ) {
      const message = this.messageQueue.shift();
      this.ws.send(JSON.stringify(message));
    }
  }

  /**
   * Start heartbeat ping interval
   */
  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send("ping");
      }
    }, this.heartbeatFrequency);
  }

  /**
   * Stop heartbeat ping interval
   */
  _stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Send pong response to server heartbeat
   */
  _sendPong() {
    this.send({ type: "pong" });
  }

  /**
   * Attempt to reconnect with exponential backoff (unlimited retries)
   */
  _attemptReconnect() {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }

    // Don't attempt reconnect if page is hidden (save resources)
    if (document.hidden) {
      this._setConnectionState(CONNECTION_STATES.RECONNECTING);
      console.log("[WebSocket] Page hidden, will reconnect when visible");
      return;
    }

    this._setConnectionState(CONNECTION_STATES.RECONNECTING);

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s (continues indefinitely)
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay,
    );

    console.log(
      `[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`,
    );

    const epoch = this.connectionEpoch;
    this.reconnectTimeoutId = setTimeout(async () => {
      try {
        this.reconnectAttempts++;
        const { token, profileId } = (await this._getCredentials()) || {};
        // disconnect() may have run while we were awaiting credentials.
        if (epoch !== this.connectionEpoch || this.connectionState === CONNECTION_STATES.DISCONNECTED) return;
        if (token) {
          this.connect(token, profileId);
        } else {
          console.warn(
            "[WebSocket] No valid credentials available, stopping reconnect",
          );
          this._setConnectionState(CONNECTION_STATES.DISCONNECTED);
        }
      } catch (error) {
        console.error("[WebSocket] Reconnect tick failed:", error);
      }
    }, delay);
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    console.log("[WebSocket] Disconnecting...");
    this.connectionEpoch++;

    // Clear reconnect timeout
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    this._stopHeartbeat();
    this._clearAuthTimeout();
    this._setConnectionState(CONNECTION_STATES.DISCONNECTED);

    if (this.ws) {
      // Close with normal closure code
      this.ws.close(1000, "Client disconnecting");
      this.ws = null;
    }

    this.token = null;
    this.reconnectAttempts = 0;
    this.messageQueue = [];
  }

  /**
   * Complete cleanup for the service
   */
  destroy() {
    this.disconnect();
    document.removeEventListener(
      "visibilitychange",
      this._handleVisibilityChange,
    );
  }

  /**
   * Reset connection - useful when token or profile changes
   * @param {string} token - JWT token for authentication
   * @param {string|number|null} profileId - Optional Amazon Ads profile ID
   */
  reconnect(token, profileId) {
    this.disconnect();
    if (token) {
      this.connect(token, profileId);
    }
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();

// Export class for testing or custom instances
export { WebSocketService, CONNECTION_STATES };
